// Sprint 9.5 — Theme presets.
// Each preset overrides 4 CSS HSL variables on :root via data-theme attribute.
// Drop a fifth/sixth preset by adding one entry — no other code changes needed.

export const THEMES = [
  {
    key: "default",
    label: "Default · Friendly",
    swatch: { main: "#eef0f4", side: "#1e293b" },
    vars: {
      "--background": "220 16% 95%",
      "--surface": "220 16% 95%",
      "--sidebar-bg": "215 28% 17%",
      "--sidebar-fg": "220 13% 91%",
      "--primary": "222 100% 33%",
    },
  },
  {
    key: "slate",
    label: "Slate · Dark sidebar",
    swatch: { main: "#f8fafc", side: "#0f172a" },
    vars: {
      "--background": "210 40% 98%",
      "--surface": "210 40% 98%",
      "--sidebar-bg": "222 47% 11%",
      "--sidebar-fg": "210 40% 96%",
      "--primary": "222 100% 33%",
    },
  },
  {
    key: "forest",
    label: "Forest · Emerald",
    swatch: { main: "#ecfdf5", side: "#022c22" },
    vars: {
      "--background": "150 60% 97%",
      "--surface": "150 60% 97%",
      "--sidebar-bg": "164 86% 9%",
      "--sidebar-fg": "150 50% 95%",
      "--primary": "160 84% 32%",
    },
  },
  {
    key: "sunset",
    label: "Sunset · Rose",
    swatch: { main: "#fff7ed", side: "#4c0519" },
    vars: {
      "--background": "33 100% 96%",
      "--surface": "33 100% 96%",
      "--sidebar-bg": "343 88% 16%",
      "--sidebar-fg": "33 100% 95%",
      "--primary": "346 77% 50%",
    },
  },
  {
    key: "midnight",
    label: "Midnight · Violet",
    swatch: { main: "#fafafa", side: "#0a0a0a" },
    vars: {
      "--background": "0 0% 98%",
      "--surface": "0 0% 98%",
      "--sidebar-bg": "240 10% 4%",
      "--sidebar-fg": "0 0% 95%",
      "--primary": "262 83% 58%",
    },
  },
  // ─── Lighter palettes ───────────────────────────────────────────────────
  {
    key: "sky",
    label: "Sky · Cloud",
    swatch: { main: "#f0f9ff", side: "#dbeafe" },
    vars: {
      "--background": "204 100% 97%",
      "--surface": "204 100% 97%",
      "--sidebar-bg": "214 95% 93%",
      "--sidebar-fg": "222 47% 18%",
      "--primary": "210 100% 45%",
    },
  },
  {
    key: "lavender",
    label: "Lavender · Mist",
    swatch: { main: "#faf5ff", side: "#ede9fe" },
    vars: {
      "--background": "270 100% 98%",
      "--surface": "270 100% 98%",
      "--sidebar-bg": "262 60% 94%",
      "--sidebar-fg": "262 40% 20%",
      "--primary": "262 83% 58%",
    },
  },
  {
    key: "peach",
    label: "Peach · Cream",
    swatch: { main: "#fff7ed", side: "#ffedd5" },
    vars: {
      "--background": "33 100% 97%",
      "--surface": "33 100% 97%",
      "--sidebar-bg": "28 100% 92%",
      "--sidebar-fg": "20 50% 20%",
      "--primary": "20 90% 50%",
    },
  },
  {
    key: "mint",
    label: "Mint · Fresh",
    swatch: { main: "#f0fdf4", side: "#dcfce7" },
    vars: {
      "--background": "138 76% 97%",
      "--surface": "138 76% 97%",
      "--sidebar-bg": "141 79% 92%",
      "--sidebar-fg": "150 40% 18%",
      "--primary": "160 84% 36%",
    },
  },
  {
    key: "sand",
    label: "Sand · Warm",
    swatch: { main: "#fefce8", side: "#fef3c7" },
    vars: {
      "--background": "55 92% 95%",
      "--surface": "55 92% 95%",
      "--sidebar-bg": "48 96% 89%",
      "--sidebar-fg": "30 40% 20%",
      "--primary": "36 90% 45%",
    },
  },
  {
    key: "blush",
    label: "Blush · Soft",
    swatch: { main: "#fff1f2", side: "#ffe4e6" },
    vars: {
      "--background": "356 100% 97%",
      "--surface": "356 100% 97%",
      "--sidebar-bg": "351 100% 94%",
      "--sidebar-fg": "346 50% 22%",
      "--primary": "330 81% 55%",
    },
  },
  {
    key: "pearl",
    label: "Pearl · Ivory",
    swatch: { main: "#fafaf9", side: "#f5f5f4" },
    vars: {
      "--background": "60 9% 98%",
      "--surface": "60 9% 98%",
      "--sidebar-bg": "60 5% 96%",
      "--sidebar-fg": "24 10% 18%",
      "--primary": "220 90% 56%",
    },
  },
];

const STORAGE_KEY = "kampus365.theme";

export function getStoredThemeKey() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "default";
  } catch {
    return "default";
  }
}

export function applyTheme(key) {
  const theme = THEMES.find((t) => t.key === key) || THEMES[0];
  const root = document.documentElement;
  for (const [k, v] of Object.entries(theme.vars)) root.style.setProperty(k, v);
  root.setAttribute("data-theme", theme.key);
  try {
    localStorage.setItem(STORAGE_KEY, theme.key);
  } catch {
    // ignore
  }
  return theme.key;
}

export function initTheme() {
  applyTheme(getStoredThemeKey());
}
